import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import Screenshot from "./Screenshot";
import { slugifyHeading } from "./markdownHeadings";

type MarkdownArticleProps = {
    markdown: string;
    /** Suppresses interactive affordances and switches to the print palette. */
    print?: boolean;
};

const childrenToText = (children: ReactNode): string => {
    if (children === null || children === undefined || typeof children === "boolean") return "";
    if (typeof children === "string" || typeof children === "number") return String(children);
    if (Array.isArray(children)) return children.map(childrenToText).join("");
    if (typeof children === "object" && "props" in children) {
        return childrenToText((children as { props?: { children?: ReactNode } }).props?.children);
    }
    return "";
};

/** `![Step 2 — Open the Widgets tab](…)` drives the numbered badge. */
const parseAlt = (alt: string | undefined): { step?: number; caption?: string } => {
    if (!alt) return {};
    const match = /^Step\s+(\d+)\s*[—–:.-]\s*(.*)$/i.exec(alt.trim());
    if (!match) return { caption: alt };
    return { step: Number(match[1]), caption: match[2].trim() || undefined };
};

const isExternal = (href: string) => /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");

/**
 * Renders a docs markdown document with the site's dark palette, plus a
 * light-on-white variant for print. `@tailwindcss/typography` is not installed,
 * so every element is styled through the `components` map rather than `prose`.
 */
export default function MarkdownArticle({ markdown, print = false }: MarkdownArticleProps) {
    const components = useMemo<Components>(
        () => ({
            // The article page already renders the section title as the only h1.
            h1: ({ children }) => (
                <h2 className="mt-0 mb-5 text-3xl font-bold text-white print:text-2xl print:text-black">{children}</h2>
            ),
            h2: ({ children }) => {
                const text = childrenToText(children);
                return (
                    <h2
                        id={slugifyHeading(text)}
                        className="group mt-12 mb-4 scroll-mt-24 border-b border-slate-800 pb-2 text-2xl font-semibold text-white print:mt-8 print:break-after-avoid print:border-slate-300 print:text-xl print:text-black"
                    >
                        {children}
                        {print ? null : (
                            <a
                                href={`#${slugifyHeading(text)}`}
                                className="ml-2 text-slate-600 opacity-0 transition-opacity group-hover:opacity-100"
                                aria-label={`Link to ${text}`}
                            >
                                #
                            </a>
                        )}
                    </h2>
                );
            },
            h3: ({ children }) => {
                const text = childrenToText(children);
                return (
                    <h3
                        id={slugifyHeading(text)}
                        className="mt-8 mb-3 scroll-mt-24 text-lg font-semibold text-slate-100 print:mt-6 print:break-after-avoid print:text-base print:text-black"
                    >
                        {children}
                    </h3>
                );
            },
            h4: ({ children }) => (
                <h4 className="mt-6 mb-2 text-base font-semibold text-slate-200 print:break-after-avoid print:text-black">{children}</h4>
            ),
            p: ({ node, children }) => {
                // A `<figure>` may not sit inside a `<p>`, so a paragraph that
                // holds nothing but an image unwraps to its children.
                const meaningful = (node?.children ?? []).filter(
                    (child) => !(child.type === "text" && child.value.trim() === ""),
                );
                if (meaningful.length === 1 && meaningful[0].type === "element" && meaningful[0].tagName === "img") {
                    return <>{children}</>;
                }
                return <p className="my-4 leading-7 text-slate-300 print:my-3 print:leading-6 print:text-slate-800">{children}</p>;
            },
            ul: ({ children }) => <ul className="my-4 list-disc space-y-2 pl-6 text-slate-300 print:my-3 print:text-slate-800">{children}</ul>,
            ol: ({ children }) => <ol className="my-4 list-decimal space-y-2 pl-6 text-slate-300 print:my-3 print:text-slate-800">{children}</ol>,
            li: ({ children }) => <li className="leading-7 print:leading-6">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-white print:text-black">{children}</strong>,
            em: ({ children }) => <em className="italic text-slate-200 print:text-slate-800">{children}</em>,
            hr: () => <hr className="my-10 border-slate-800 print:my-6 print:border-slate-300" />,
            blockquote: ({ children }) => (
                <blockquote className="my-5 rounded-r-lg border-l-4 border-orange-500/70 bg-slate-900/60 py-3 pl-4 pr-4 text-slate-300 print:break-inside-avoid print:bg-slate-100 print:text-slate-800">
                    {children}
                </blockquote>
            ),
            a: ({ href, children }) => {
                if (!href) return <>{children}</>;
                if (isExternal(href)) {
                    return (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 transition hover:text-orange-300 print:text-slate-900"
                        >
                            {children}
                        </a>
                    );
                }
                if (href.startsWith("#")) {
                    return (
                        <a href={href} className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 print:text-slate-900">
                            {children}
                        </a>
                    );
                }
                return (
                    <Link to={href} className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 transition hover:text-orange-300 print:text-slate-900">
                        {children}
                    </Link>
                );
            },
            img: ({ src, alt }) => {
                const { step, caption } = parseAlt(alt);
                return <Screenshot src={typeof src === "string" ? src : ""} caption={caption} step={step} print={print} />;
            },
            code: ({ className, children }) => {
                const isBlock = typeof className === "string" && className.startsWith("language-");
                if (isBlock) {
                    return <code className="block whitespace-pre text-sm leading-6 text-slate-200 print:text-slate-900">{children}</code>;
                }
                return (
                    <code className="rounded bg-slate-800/80 px-1.5 py-0.5 font-mono text-[0.85em] text-orange-300 print:bg-slate-200 print:text-slate-900">
                        {children}
                    </code>
                );
            },
            pre: ({ children }) => (
                <pre className="my-5 overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/80 p-4 print:break-inside-avoid print:border-slate-300 print:bg-slate-100 print:p-3">
                    {children}
                </pre>
            ),
            table: ({ children }) => (
                <div className="my-6 overflow-x-auto rounded-xl border border-slate-800 print:break-inside-avoid print:border-slate-300">
                    <table className="w-full border-collapse text-left text-sm">{children}</table>
                </div>
            ),
            thead: ({ children }) => <thead className="bg-slate-900/80 print:bg-slate-100">{children}</thead>,
            th: ({ children }) => (
                <th className="border-b border-slate-800 px-4 py-2.5 font-semibold text-slate-200 print:border-slate-300 print:text-black">{children}</th>
            ),
            td: ({ children }) => (
                <td className="border-b border-slate-800/70 px-4 py-2.5 align-top text-slate-300 print:border-slate-200 print:text-slate-800">{children}</td>
            ),
        }),
        [print],
    );

    return (
        <div className="docs-article">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {markdown}
            </ReactMarkdown>
        </div>
    );
}
